import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Calculator as CalcIcon, AlertTriangle, Info, ArrowRight,
  Home, Briefcase, Building, Car, Heart, FileBadge, Banknote, Receipt,
  CheckCircle2, Scroll, Scale,
} from "lucide-react";
import { api, formatApiError, formatZAR } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import CampaignBanner from "@/components/CampaignBanner";
import CalculatorSwitcher from "@/components/CalculatorSwitcher";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// EstateCalculator — public deceased-estate calculator at /calculator/deceased-estate.
// Mirrors the design language of MaintenanceCalculator: editorial hero, sectioned
// input form, smart result panel with range + narrative + readiness + liquidity
// insight + "what happens next" + Lead-first CTA.
// ---------------------------------------------------------------------------

const DEFAULT_FORM = {
  assets: {
    property:        "",
    bank_accounts:   "",
    investments:     "",
    vehicles:        "",
    personal_assets: "",
  },
  deemed_property: {
    life_policies:       "",
    retirement_benefits: "",
    accrual_claims:      "",
    other_deemed:        "",
  },
  liabilities: {
    bonds:         "",
    loans:         "",
    tax:           "",
    funeral_costs: "",
  },
  available_cash:    "",
  post_death_income: "",
  estate_duty: {
    allowable_deductions: "",
    spousal_deduction:    "",
    spousal_rollover:     false,
  },
  executor: {
    vat_registered: false,
  },
};


export default function EstateCalculator() {
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [proformaLoading, setProformaLoading] = useState(false);

  const updateNested = (path, value) =>
    setForm((curr) => {
      const next = { ...curr };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...(ref[keys[i]] || {}) };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });

  const toNum = (v) => (v === "" || v === null ? 0 : Number(v) || 0);

  const handleCalc = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        assets: Object.fromEntries(Object.entries(form.assets).map(([k, v]) => [k, toNum(v)])),
        deemed_property: Object.fromEntries(Object.entries(form.deemed_property).map(([k, v]) => [k, toNum(v)])),
        liabilities: Object.fromEntries(Object.entries(form.liabilities).map(([k, v]) => [k, toNum(v)])),
        available_cash:    { amount: toNum(form.available_cash) },
        post_death_income: { total:  toNum(form.post_death_income) },
        estate_duty: {
          allowable_deductions: toNum(form.estate_duty.allowable_deductions),
          spousal_deduction:    toNum(form.estate_duty.spousal_deduction),
          spousal_rollover:     !!form.estate_duty.spousal_rollover,
        },
        executor: { vat_registered: !!form.executor.vat_registered },
      };
      const { data } = await api.post("/calc/deceased-estate", payload);
      setResult(data);
      window.requestAnimationFrame(() => {
        document.getElementById("estate-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!contactName.trim() || !contactEmail.trim()) {
      toast.error("Please add your name and email to continue.");
      return;
    }
    setProformaLoading(true);
    try {
      const { data } = await api.post("/proforma/issue", {
        name: contactName.trim(),
        email: contactEmail.trim().toLowerCase(),
        phone: contactPhone.trim() || null,
        matter_type: "deceased_estate",
        pricing_mode: "managed",
        metadata: {
          source:               "deceased_estate_calculator",
          gross_assets:         result?.gross_assets,
          gross_estate:         result?.gross_estate,
          net_estate:           result?.net_estate,
          executor_fees:        result?.executor_fees?.total,
          estate_duty:          result?.estate_duty?.duty,
          total_cost_mid:       result?.total_cost?.mid,
          total_cost_label:     result?.total_cost?.label,
          liquidity_level:      result?.liquidity?.level,
          liquidity_label:      result?.liquidity?.label,
          shortfall_high:       result?.liquidity?.shortfall_high,
          rescue_options:       (result?.rescue_options || []).map((o) => ({
            id: o.id, title: o.title, complexity: o.complexity, timing: o.timing,
          })),
        },
      });
      toast.success("Pro-forma issued — let's complete the full estate breakdown.");
      navigate(`/proforma/${data.proforma_id || data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProformaLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <SiteHeader />
      <CampaignBanner kicker="EH Inc · Deceased Estate Calculator" />
      <CalculatorSwitcher active="estate" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {/* Hero */}
        <header className="mb-10 max-w-3xl" data-testid="estate-calc-hero">
          <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-amber-700">
            <Scroll className="h-3.5 w-3.5" /> Deceased Estate · Administration Calculator
          </div>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight text-zinc-900">
            How much will it cost to wind up this estate?
          </h1>
          <p className="mt-5 text-zinc-600 leading-relaxed">
            An indicative estimate of executor's fees, estate duty, and administration costs — calibrated to South African practice (Administration of Estates Act + Estate Duty Act). The headline insight is <span className="font-medium">whether the estate has enough cash to cover its costs</span>.
          </p>
          <div className="mt-3 text-xs text-zinc-500 inline-flex items-center gap-1.5" data-testid="estate-global-note">
            <Info className="h-3.5 w-3.5" /> Enter best estimates — you can refine later when full administration begins.
          </div>
        </header>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Calculator column */}
          <form className="lg:col-span-6 bg-white border border-zinc-200 p-8 lg:p-10 space-y-8" onSubmit={handleCalc} data-testid="estate-calc-form">

            <Section icon={<Home className="h-3.5 w-3.5" />} title="1. Estate assets" helper="Everything the deceased owned at the date of death.">
              <Grid>
                <ZARField label="Property"        value={form.assets.property}        onChange={(v) => updateNested("assets.property", v)}        testid="estate-asset-property" />
                <ZARField label="Bank accounts"   value={form.assets.bank_accounts}   onChange={(v) => updateNested("assets.bank_accounts", v)}   testid="estate-asset-bank" />
                <ZARField label="Investments"     value={form.assets.investments}     onChange={(v) => updateNested("assets.investments", v)}     testid="estate-asset-investments" />
                <ZARField label="Vehicles"        value={form.assets.vehicles}        onChange={(v) => updateNested("assets.vehicles", v)}        testid="estate-asset-vehicles" />
                <ZARField label="Personal assets" value={form.assets.personal_assets} onChange={(v) => updateNested("assets.personal_assets", v)} testid="estate-asset-personal" />
              </Grid>
            </Section>

            <Section icon={<FileBadge className="h-3.5 w-3.5" />} title="2. Deemed property"
                       helper="Items treated as part of the estate for duty purposes — s 3 of the Estate Duty Act.">
              <Grid>
                <ZARField label="Life policies"        value={form.deemed_property.life_policies}        onChange={(v) => updateNested("deemed_property.life_policies", v)}        testid="estate-deemed-life" />
                <ZARField label="Retirement benefits"  value={form.deemed_property.retirement_benefits}  onChange={(v) => updateNested("deemed_property.retirement_benefits", v)}  testid="estate-deemed-retirement" />
                <ZARField label="Accrual claims"       value={form.deemed_property.accrual_claims}       onChange={(v) => updateNested("deemed_property.accrual_claims", v)}       testid="estate-deemed-accrual" />
                <ZARField label="Other deemed"         value={form.deemed_property.other_deemed}         onChange={(v) => updateNested("deemed_property.other_deemed", v)}         testid="estate-deemed-other" />
              </Grid>
            </Section>

            <Section icon={<Receipt className="h-3.5 w-3.5" />} title="3. Liabilities" helper="Debts owed by the estate — reduces both the dutiable amount and net distribution.">
              <Grid>
                <ZARField label="Outstanding bond(s)" value={form.liabilities.bonds}         onChange={(v) => updateNested("liabilities.bonds", v)}         testid="estate-liab-bonds" />
                <ZARField label="Loans"              value={form.liabilities.loans}         onChange={(v) => updateNested("liabilities.loans", v)}         testid="estate-liab-loans" />
                <ZARField label="Outstanding tax"    value={form.liabilities.tax}           onChange={(v) => updateNested("liabilities.tax", v)}           testid="estate-liab-tax" />
                <ZARField label="Funeral costs"      value={form.liabilities.funeral_costs} onChange={(v) => updateNested("liabilities.funeral_costs", v)} testid="estate-liab-funeral" />
              </Grid>
            </Section>

            <Section icon={<Banknote className="h-3.5 w-3.5" />} title="4. Available cash" helper="Liquid funds the executor can access immediately (cash on hand, transmission accounts).">
              <ZARField label="Immediately accessible" value={form.available_cash} onChange={(v) => updateNested("available_cash", v)} testid="estate-available-cash" />
            </Section>

            <Section icon={<Briefcase className="h-3.5 w-3.5" />} title="5. Post-death income (monthly estimate)" helper="Income accruing after date of death — rental, interest, dividends. Used for the 6% executor's fee on income.">
              <ZARField label="Total monthly income" value={form.post_death_income} onChange={(v) => updateNested("post_death_income", v)} testid="estate-post-death-income" />
            </Section>

            <Section icon={<Scale className="h-3.5 w-3.5" />} title="6. Estate-duty inputs" helper="Optional. Spousal deduction (s 4(q)) and rollover (s 4A) can dramatically change the duty owed.">
              <Grid>
                <ZARField label="Allowable deductions"     value={form.estate_duty.allowable_deductions} onChange={(v) => updateNested("estate_duty.allowable_deductions", v)} testid="estate-duty-deductions" />
                <ZARField label="Spousal deduction (s 4(q))" value={form.estate_duty.spousal_deduction} onChange={(v) => updateNested("estate_duty.spousal_deduction", v)} testid="estate-duty-spousal" />
              </Grid>
              <label className="flex items-center gap-2 mt-3 text-sm text-zinc-700 cursor-pointer" data-testid="estate-duty-rollover-label">
                <input type="checkbox" checked={!!form.estate_duty.spousal_rollover} onChange={(e) => updateNested("estate_duty.spousal_rollover", e.target.checked)} data-testid="estate-duty-rollover" />
                <span>Apply spousal rollover (R7m combined § 4A abatement)</span>
              </label>
              <label className="flex items-center gap-2 mt-1 text-sm text-zinc-700 cursor-pointer">
                <input type="checkbox" checked={!!form.executor.vat_registered} onChange={(e) => updateNested("executor.vat_registered", e.target.checked)} data-testid="estate-executor-vat" />
                <span>Executor is VAT-registered (adds 15% VAT to fees)</span>
              </label>
            </Section>

            {error && <div className="text-sm text-red-600" data-testid="estate-calc-error">{error}</div>}

            <button type="submit" disabled={loading}
                       className="w-full bg-zinc-900 text-white px-6 py-4 font-medium hover:bg-zinc-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                       data-testid="estate-calc-submit">
              <CalcIcon className="h-4 w-4" /> {loading ? "Calculating…" : "Calculate estate administration costs →"}
            </button>
          </form>

          {/* Result column */}
          <div className="lg:col-span-6 space-y-6" id="estate-result">
            {!result ? (
              <div className="h-full min-h-[520px] border border-zinc-200 bg-white p-10 lg:p-12 grid place-items-center text-center">
                <div>
                  <Sparkles className="h-8 w-8 text-zinc-300 mx-auto" />
                  <p className="mt-4 text-zinc-500 max-w-sm mx-auto leading-relaxed">
                    Enter the deceased's assets, liabilities and a few estate-duty inputs to see executor's fees, duty, and the all-important liquidity check.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <LiquidityPanel liquidity={result.liquidity} />
                <RescuePanel options={result.rescue_options} liquidityLevel={result.liquidity.level} />
                <HeadlinePanel result={result} />
                <NarrativePanel narrative={result.narrative} />
                <ReadinessPanel readiness={result.readiness} />
                <NextStepsPanel />
                <ContinuePanel
                  contactName={contactName} setContactName={setContactName}
                  contactEmail={contactEmail} setContactEmail={setContactEmail}
                  contactPhone={contactPhone} setContactPhone={setContactPhone}
                  onContinue={handleContinue}
                  proformaLoading={proformaLoading}
                />
              </>
            )}
          </div>
        </div>

        <p className="mt-12 text-xs text-zinc-500 max-w-2xl leading-relaxed" data-testid="estate-disclaimer-footer">
          {result?.disclaimer || "This is an estimate. Final values depend on formal administration and SARS assessment."} Estate administration is governed by the Administration of Estates Act 66 of 1965; estate duty by the Estate Duty Act 45 of 1955.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}


// ---------------------------------------------------------------------------
// LiquidityPanel — the KEY insight, visually loud per spec.
// ---------------------------------------------------------------------------

function LiquidityPanel({ liquidity }) {
  const styles = {
    sufficient: { bg: "bg-emerald-50",  border: "border-emerald-300", icon: <CheckCircle2 className="h-6 w-6 text-emerald-700" />, head: "text-emerald-900" },
    borderline: { bg: "bg-amber-50",    border: "border-amber-300",   icon: <AlertTriangle className="h-6 w-6 text-amber-700" />, head: "text-amber-900" },
    shortfall:  { bg: "bg-red-50",      border: "border-red-300",     icon: <AlertTriangle className="h-6 w-6 text-red-700" />,   head: "text-red-900" },
  }[liquidity.level] || {};
  return (
    <div className={`border-2 ${styles.border} ${styles.bg} p-6`} data-testid={`estate-liquidity-${liquidity.level}`}>
      <div className="flex items-start gap-3">
        {styles.icon}
        <div className="flex-1">
          <div className="text-overline text-xs tracking-[0.18em] uppercase text-zinc-500">Liquidity assessment</div>
          <div className={`mt-1 font-serif text-2xl lg:text-3xl font-medium ${styles.head}`} data-testid="estate-liquidity-label">
            {liquidity.label}
          </div>
          <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{liquidity.detail}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <Stat label="Available cash"      value={formatZAR(liquidity.available_cash)} />
            <Stat label="Accessible (cash + bank + 12mo income)" value={formatZAR(liquidity.accessible_total)} />
            <Stat label="Estimated cost (low)"  value={formatZAR(liquidity.estimated_cost_low)} />
            <Stat label="Estimated cost (high)" value={formatZAR(liquidity.estimated_cost_high)} />
            {liquidity.shortfall_high > 0 && (
              <Stat label="Potential shortfall (high)" value={formatZAR(liquidity.shortfall_high)} emphasis />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RescuePanel({ options, liquidityLevel }) {
  if (!options || options.length === 0) return null;
  const complexityColor = {
    simple:   "bg-emerald-50 text-emerald-700",
    moderate: "bg-amber-50  text-amber-700",
    complex:  "bg-red-50    text-red-700",
  };
  return (
    <div className="border border-zinc-200 bg-white" data-testid="estate-rescue-panel">
      <div className="px-6 pt-5 pb-3 border-b border-zinc-200">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">How to solve this</div>
        <div className="mt-1 font-serif text-xl text-zinc-900">
          {liquidityLevel === "shortfall"
            ? "There's a cash shortfall — here are 4 ways to solve it."
            : "Cash is tight — these options keep the administration moving."}
        </div>
        <div className="text-xs text-zinc-500 mt-1">Your attorney will help you pick the right path.</div>
      </div>
      <ol className="divide-y divide-zinc-200">
        {options.map((opt, idx) => (
          <li key={opt.id}
              className={`p-5 ${!opt.applicable ? "opacity-60" : ""}`}
              data-testid={`rescue-option-${opt.id}`}>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 h-7 w-7 rounded-full bg-zinc-900 text-white text-xs flex items-center justify-center mt-0.5">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <span className="font-medium text-zinc-900 text-sm">{opt.title}</span>
                  <span className={`text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 ${complexityColor[opt.complexity] || "bg-zinc-100 text-zinc-700"}`}
                           data-testid={`rescue-${opt.id}-complexity`}>
                    {opt.complexity}
                  </span>
                  {!opt.applicable && (
                    <span className="text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 bg-zinc-100 text-zinc-500">
                      Not applicable to this estate
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed">{opt.explanation}</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                  <span><strong className="text-zinc-700">Timing:</strong> {opt.timing}</span>
                  {opt.cost_note && <span><strong className="text-zinc-700">Cost:</strong> {opt.cost_note}</span>}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}


function HeadlinePanel({ result }) {  return (
    <div className="border border-zinc-200 bg-white" data-testid="estate-result-panel">
      <div className="p-6 lg:p-8 bg-zinc-900 text-white">
        <div className="text-overline text-zinc-400 text-xs tracking-[0.18em] uppercase">Total administration cost</div>
        <div className="mt-2 font-serif text-4xl lg:text-5xl font-medium leading-none tracking-tight" data-testid="estate-total-cost">
          {result.total_cost.label}
        </div>
        <div className="mt-1 text-sm text-zinc-300" data-testid="estate-total-cost-mid">
          Mid-point: {formatZAR(result.total_cost.mid)}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-200">
        <Cell label="Gross estate"   value={formatZAR(result.gross_estate)}   testid="estate-gross" />
        <Cell label="Net estate"      value={formatZAR(result.net_estate)}      testid="estate-net" />
        <Cell label="Executor fees" value={formatZAR(result.executor_fees.total)} testid="estate-executor-fee" />
        <Cell label="Estate duty"    value={formatZAR(result.estate_duty.duty)}    testid="estate-duty-amount" />
      </div>
      <div className="p-5 border-t border-zinc-200 bg-amber-50/40 text-sm text-amber-900" data-testid="estate-disclaimer-inline">
        {result.disclaimer}
      </div>
    </div>
  );
}

function NarrativePanel({ narrative }) {
  if (!narrative?.length) return null;
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="estate-narrative-panel">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">How this was calculated</div>
      <div className="space-y-3">
        {narrative.map((n) => (
          <div key={n.id} className="text-sm" data-testid={`estate-narrative-${n.id}`}>
            <div className="flex justify-between gap-3">
              <span className="text-zinc-600">{n.label}</span>
              <span className="font-medium text-zinc-900 text-right">{n.value}</span>
            </div>
            {n.detail && <div className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{n.detail}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReadinessPanel({ readiness }) {
  if (!readiness) return null;
  const pct = readiness.pct;
  const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="estate-readiness-panel">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Assessment completeness</div>
        <div className="font-serif text-xl text-zinc-900" data-testid="estate-readiness-pct">{pct}%</div>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mb-3">
        <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-zinc-600 mb-3">{readiness.headline}</div>
      <ul className="space-y-1 text-xs">
        {readiness.items.map((it) => (
          <li key={it.id} className="flex items-start gap-2" data-testid={`estate-readiness-${it.id}`}>
            {it.satisfied
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
              : <span className={`h-3.5 w-3.5 rounded-full border flex-shrink-0 mt-0.5 ${it.severity === "Critical" ? "border-red-500" : "border-zinc-300"}`} />
            }
            <div className="flex-1">
              <div className={it.satisfied ? "text-zinc-500 line-through" : "text-zinc-800"}>{it.label}</div>
              {!it.satisfied && <div className="text-zinc-500">{it.improves}</div>}
            </div>
            <span className={`text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded ${it.severity === "Critical" ? "bg-red-50 text-red-700" : it.severity === "Important" ? "bg-amber-50 text-amber-700" : "bg-zinc-50 text-zinc-500"}`}>{it.severity}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NextStepsPanel() {
  const STEPS = [
    { id: 1, label: "Estate reported to the Master of the High Court" },
    { id: 2, label: "Executor appointed (Letters of Executorship issued)" },
    { id: 3, label: "Assets collected; liabilities settled" },
    { id: 4, label: "Distribution to heirs per the L&D account" },
  ];
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="estate-next-steps">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">What happens next</div>
      <ol className="space-y-2">
        {STEPS.map((s) => (
          <li key={s.id} className="flex items-center gap-3 text-sm text-zinc-800" data-testid={`estate-next-step-${s.id}`}>
            <span className="flex-shrink-0 h-6 w-6 rounded-full bg-zinc-100 text-xs flex items-center justify-center text-zinc-600">{s.id}</span>
            <span>{s.label}</span>
          </li>
        ))}
      </ol>
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-3" data-testid="estate-administration-note">
        Estate administration takes 6–18 months depending on complexity and Master's queue times.
      </div>
    </div>
  );
}

function ContinuePanel({ contactName, setContactName, contactEmail, setContactEmail, contactPhone, setContactPhone, onContinue, proformaLoading }) {
  return (
    <div className="border border-zinc-200 bg-white p-6 lg:p-8" data-testid="estate-continue-panel">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Continue to full estate breakdown</div>
      <div className="text-sm text-zinc-700 mt-1 mb-4 leading-relaxed">
        We'll prepare a pro-forma and a full estate administration matter — including the formal asset register, FICA, and Master's reporting.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <input type="text"  value={contactName}  onChange={(e) => setContactName(e.target.value)}  placeholder="Your name" data-testid="estate-contact-name"  className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
        <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Email"      data-testid="estate-contact-email" className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
        <input type="tel"   value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Mobile (optional)" data-testid="estate-contact-phone" className="border border-zinc-300 px-3 py-2 text-sm bg-white" />
      </div>
      <button type="button" onClick={onContinue} disabled={proformaLoading}
                 className="w-full bg-zinc-900 text-white px-5 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
                 data-testid="estate-continue-cta">
        {proformaLoading ? "Issuing pro-forma…" : "Continue to full estate breakdown"} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Form primitives
// ---------------------------------------------------------------------------

function Section({ icon, title, helper, children }) {
  return (
    <div>
      <div className="mb-3">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase flex items-center gap-1.5">
          {icon}{title}
        </div>
        {helper && <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{helper}</div>}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>;
}

function ZARField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-zinc-500 mb-1">{label}</span>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">R</span>
        <input type="text" inputMode="numeric"
                  value={value}
                  onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
                  data-testid={testid}
                  className="w-full pl-6 pr-2 py-2 border border-zinc-300 text-sm bg-white focus:border-zinc-900 focus:outline-none" />
      </div>
    </label>
  );
}

function Stat({ label, value, emphasis }) {
  return (
    <div className={emphasis ? "p-2 bg-white/60 border border-zinc-300" : ""}>
      <div className="text-zinc-500">{label}</div>
      <div className={`font-medium ${emphasis ? "text-red-700" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function Cell({ label, value, testid }) {
  return (
    <div className="p-4 bg-white" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-serif text-xl text-zinc-900">{value}</div>
    </div>
  );
}
